import { h, Component, Prop, Element, Listen, Method, Host } from '@stencil/core';
import { LightStreamerSubscription as LS } from '../../utils/LightStreamerSubscription.js';
import { Page } from '../../utils/Page.js';
import { checkLocal } from '../../utils/checkLocal.js';
import { worldClock } from '../../libs/worldClock.js';

declare global {
  interface Window {
    PS: any;
  }
}
const PS = window.PS;

@Component({
  shadow: true,
  styleUrls: {
    default: 'igws-live-prices.default.less',
    rtl: 'igws-live-prices.rtl.less',
    rebrand: 'igws-live-prices.default.less',
    rtl_rebrand: 'igws-live-prices.rtl.less',
    prime: 'igws-live-prices.default.less',
    rtl_prime: 'igws-live-prices.rtl.less',
    investments: 'igws-live-prices.default.less',
    rtl_investments: 'igws-live-prices.rtl.less',
    helpandsupport: 'igws-live-prices.default.less',
    rtl_helpandsupport: 'igws-live-prices.rtl.less',
    academy: 'igws-live-prices.default.less',
    rtl_academy: 'igws-live-prices.rtl.less',
    landingpages: 'igws-live-prices.default.less',
    rtl_landingpages: 'igws-live-prices.rtl.less',
    generic: 'igws-live-prices.default.less',
    rtl_generic: 'igws-live-prices.rtl.less',
  },
  tag: 'igws-live-prices',
})
export class LivePricesComponent {
  private windowWidth = 0;
  private timeoutID: any;
  private epicsValues!: string[][];
  private nameUrlValues!: string[][];
  private fidsValues!: string[][];
  private scalingFactor = {};
  private price!: HTMLElement;

  @Element() el!: HTMLIgwsLivePricesElement;

  /**
   * Component epics to connect
   */
  @Prop() epics!: string;

  /**
   * Markets names and urls
   */
  @Prop() nameUrl!: string;

  /**
   * Component fids
   */
  @Prop() fids!: string;

  /**
   * Remove links from component
   */
  @Prop() removeLinks!: boolean;

  /**
   * Remove links for mobile breakpoint
   */
  @Prop() removeMobileLinks!: boolean;

  /**
   * Adjust country time value
   */
  @Prop() countryTime!: boolean;

  /**
   * CMS edit mode
   */
  @Prop() editMode!: boolean;

  /**
   * Use reskin styling
   */
  @Prop() isReskin = false;

  @Listen('resize', { target: 'window' })
  handleResize(ev: UIEvent) {
    window.clearTimeout(this.timeoutID);
    this.timeoutID = window.setTimeout(() => {
      const target = ev.target as Window;
      const currentwidth =
        target.innerWidth || target.document.documentElement.clientWidth || target.document.body.clientWidth;
      if (currentwidth !== this.windowWidth) {
        this.windowWidth = currentwidth;
        this.resize();
      }
    }, 200);
  }

  componentWillLoad() {
    this.windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    this.epicsValues = this.convertValues(this.epics);
    this.nameUrlValues = this.convertValues(this.nameUrl);
    this.fidsValues = this.convertValues(this.fids);
  }

  componentDidLoad() {
    this.price = this.el.shadowRoot?.querySelector('.dynamic-table') as HTMLElement;
    if (!this.editMode) {
      this.update = this.update.bind(this); // Force scope
      LS.loadScript(this.setup.bind(this));
    }
  }

  private convertValues(str: string): string[][] {
    return str
      .replace(/\{|\}|/gi, '')
      .split(',')
      .map(f => f.split('=').map(s => s.trim()));
  }

  private getEpicID(index: any) {
    const epicsArray = this.epics.slice(1, -1).split(', ');
    return epicsArray[index].split('=')[0];
  }

  private setup() {
    this.epicsValues.forEach((epic, index) => {
      const modifier = epic[1] !== '' ? `:${epic[1].replace(/_/gi, ',')}` : '';
      LS.subscribe(epic[0], modifier, this.price, this.update, index);
    });

    this.resize();
  }

  private update(price: HTMLElement, fieldName: string, _fieldPos: any, newValue: string, index: number) {
    const field = price.querySelectorAll(`[data-field$=${fieldName}]`)[index] as HTMLElement;
    const nValue = Number(newValue);
    if (fieldName === 'SCALING_FACTOR') {
      this.scalingFactor[index] = newValue || 1;
    }

    /* istanbul ignore else */
    if (field !== null) {
      let appliedClass = '';
      field.classList.remove('price--up', 'price--down');
      switch (fieldName) {
        case 'BID':
        case 'OFR':
          const oldValue = Number(field.innerText);
          if (oldValue > nValue) {
            appliedClass = 'price--down';
          } else if (oldValue < nValue) {
            appliedClass = 'price--up';
          }
          break;
        case 'CPT':
        case 'CPC':
          if (nValue < 0) {
            field.classList.add('price--down');
          } else if (nValue > 0) {
            field.classList.add('price--up');
          }
          break;
        case 'UTM':
          const adjustCountryTime = field.hasAttribute('data-time');
          if (adjustCountryTime) {
            const countryLocalTime = checkLocal();
            newValue = worldClock(countryLocalTime[0], countryLocalTime[1]);
          }
          break;
        default:
          console.log('Live prices: field default behaviour');
      }

      if (appliedClass !== '') {
        field.classList.add(appliedClass);

        if ((field as any)._priceTimeout) {
          clearTimeout((field as any)._priceTimeout);
        }

        (field as any)._priceTimeout = setTimeout(() => {
          field.classList.remove('price--up', 'price--down');
        }, 500);
      }

      field.innerText = newValue;

      if (fieldName === 'BID' || fieldName === 'OFR' || fieldName === 'SPRD') {
        this.setSpread(price, index);
      }
    }
  }
  /** Resize */
  @Method()
  async resize() {
    this.showColumns();
    this.setMobileView();
  }

  private isElementWiderThanParent(child: HTMLElement) {
    const html = document.documentElement;
    const childRect = child.getBoundingClientRect();
    const parentRect = this.el.getBoundingClientRect();
    return (
      childRect.left < parentRect.left ||
      childRect.right > parentRect.right ||
      childRect.left < 0 ||
      childRect.right > (html.clientWidth || window.innerWidth)
    );
  }

  private setMobileView() {
    if (this.isElementWiderThanParent(this.price)) this.price.classList.add('dynamic-table--mobile');
  }

  private showColumns() {
    this.price.classList.remove('dynamic-table--mobile');
  }

  private handleRowClick(event: UIEvent) {
    const element = event && (event.target as IGHTMLElement);
    const isMobile = window.matchMedia('only screen and (max-width: 800px)').matches;
    if (this.removeMobileLinks && isMobile) {
      event.preventDefault();
    } else {
      const row = element.closest('.dynamic-table__row') as HTMLElement;
      const link = row.querySelector('.market-epic-link') as HTMLAnchorElement;
      const trackingData = {
        name: link.textContent,
        epicID: link.getAttribute('data-epic'),
      };
      if (link && link.tagName === 'A') {
        Page.setLocationHref(link.getAttribute('href'));
        PS.GAtracking.marketDataInteractionEvent.prototype.setEvent(trackingData);
      }
    }
  }

  private setSpread(price: HTMLElement, index: number) {
    const ofrField = price.querySelectorAll('[data-field$=OFR]')[index] as HTMLElement;
    const bidField = price.querySelectorAll('[data-field$=BID]')[index] as HTMLElement;
    const sprdField = price.querySelectorAll('[data-field$=SPRD]')[index] as HTMLElement;
    const scalingFactor = this.scalingFactor[index] || 1;
    if (sprdField !== null) {
      const ofrValue = Number(ofrField.innerText);
      const bidValue = Number(bidField.innerText);
      const sprdValue = (ofrValue - bidValue) * scalingFactor;
      // eslint-disable-next-line no-self-compare
      if (typeof sprdValue === 'number' && sprdValue === sprdValue) {
        // validate that is not NaN
        sprdField.innerText = `${+parseFloat(`${sprdValue}`).toFixed(2)}`; //  the + converts the string output of toFixed back to a float
      }
    }
  }

  render() {
    return (
      <Host>
        <section class={`dynamic-table ${this.isReskin ? 'reskin' : ''}`}>
          <header class="dynamic-table__head">
            <div class="dynamic-table__cell"></div>
            {this.fidsValues.map(fid => (
              <div class="dynamic-table__cell">{fid[1]}</div>
            ))}
          </header>
          {this.nameUrlValues.map((epic, index) => {
            return (
              <div
                class={`
                dynamic-table__row
                ${this.removeLinks ? '' : 'clickable'}
                ${this.removeMobileLinks ? 'clickable--desktop' : ''}
                `}
                onClick={this.handleRowClick}
              >
                <div class="dynamic-table__cell">
                  {this.removeLinks || epic[1] === 'null' ? (
                    <span class="market-epic-link" data-epic={this.getEpicID(index)}>
                      {epic[0]}
                    </span>
                  ) : this.removeMobileLinks ? (
                    <a href={epic[1]} data-mobile={epic[0]} class="market-epic-link" data-epic={this.getEpicID(index)}>
                      {epic[0]}
                    </a>
                  ) : (
                    <a href={epic[1]} class="market-epic-link" data-epic={this.getEpicID(index)}>
                      {epic[0]}
                    </a>
                  )}
                </div>
                {this.fidsValues.map(fid => (
                  <div class="dynamic-table__cell" data-title={fid[1]}>
                    {fid[0] === 'UTM' ? (
                      <span data-field={fid[0]} data-time={this.countryTime}>
                        -
                      </span>
                    ) : (
                      <span data-field={fid[0]}>-</span>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </section>
        <div class={`disclaimer ${this.isReskin ? 'disclaimer--reskin' : ''}`}>
          <slot name="disclaimer" />
        </div>
      </Host>
    );
  }
}
